import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

interface ProcessingConfig {
  num_colors: number;
  target_width: number;
  yarn_weight: number;
  padding_type: 'none' | 'stitches' | 'inches' | 'auto';
  padding_horizontal: number;
  padding_vertical: number;
  blanket_size: string;
  custom_width: number;
  custom_height: number;
}

interface ImageUploadProps {
  config: ProcessingConfig;
  onPatternGenerated: (data: any, file?: File) => void;
  onProcessingStart: () => void;
  processing: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  config,
  onPatternGenerated,
  onProcessingStart,
  processing
}) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    setUploadedImage(previewUrl);
    
    onProcessingStart();
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('num_colors', config.num_colors.toString());
      formData.append('target_width', config.target_width.toString());
      formData.append('yarn_weight', config.yarn_weight.toString());
      formData.append('padding_type', config.padding_type);
      formData.append('padding_horizontal', config.padding_horizontal.toString());
      formData.append('padding_vertical', config.padding_vertical.toString());
      formData.append('blanket_size', config.blanket_size);
      formData.append('custom_width', config.custom_width.toString());
      formData.append('custom_height', config.custom_height.toString());
      
      const response = await axios.post('/api/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.status === 'completed') {
        onPatternGenerated(response.data.pattern_data, file);
        // Scroll to results after a brief delay
        setTimeout(() => {
          const resultsSection = document.querySelector('.preview-section');
          if (resultsSection) {
            resultsSection.scrollIntoView({ behavior: 'smooth' });
          }
        }, 500);
      } else {
        throw new Error('Pattern generation failed');
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
      
      let errorMessage = 'Failed to process image. Please try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(errorMessage);
      onPatternGenerated(null);
      setUploadedImage(null);
    }
  }, [config, onPatternGenerated, onProcessingStart]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg']
    },
    multiple: false,
    disabled: processing
  });

  return (
    <div className="image-upload">
      <h2>Upload Your Image</h2>
      {uploadedImage && (
        <div className="image-preview">
          <img src={uploadedImage} alt="Uploaded preview" style={{
            maxWidth: '300px',
            maxHeight: '300px',
            objectFit: 'contain',
            border: '2px solid #ddd',
            borderRadius: '8px',
            marginBottom: '1rem'
          }} />
          {!processing && (
            <p style={{
              color: '#2E8B57',
              fontWeight: 'bold',
              margin: '0.5rem 0',
              fontSize: '0.9rem'
            }}>
              ✓ Image uploaded successfully! Scroll down to see your crochet pattern.
            </p>
          )}
        </div>
      )}
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'drag-active' : ''} ${processing ? 'processing' : ''}`}
      >
        <input {...getInputProps()} />
        {processing ? (
          <div className="processing-indicator">
            <div className="spinner"></div>
            <p>Processing your image...</p>
          </div>
        ) : isDragActive ? (
          <p>Drop the image here...</p>
        ) : (
          <div className="upload-prompt">
            <p>Drag & drop an image here, or click to select</p>
            <p className="file-types">Supports: PNG, JPG, JPEG (max 10MB)</p>
            <p className="file-types" style={{fontSize: '0.8rem', color: '#666', marginTop: '0.5rem'}}>
              <strong>iPhone users:</strong> Please convert HEIC photos to JPG first
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;